// Types
import type { Point, SwallowTailCapProps } from './types';

const CAP_GEOMETRY = {
  START_X_BODY: -3, // Deep overlap to hide seam artifact
  INNER_TIP_LENGTH: 26,
  V_NOTCH_INNER: 11,
} as const;

/**
 * Rotate and translate local coordinates to world space
 */
const rotatePoint = (
  x: number,
  y: number,
  cos: number,
  sin: number,
  origin: Point,
): Point => ({
  x: x * cos - y * sin + origin.x,
  y: x * sin + y * cos + origin.y,
});

/**
 * Convert points array to SVG path string
 */
const pointsToPath = (
  points: Point[],
  transformX: (x: number) => number,
  cos: number,
  sin: number,
  origin: Point,
): string => {
  const pathCommands = points
    .map((p) => {
      const rotated = rotatePoint(transformX(p.x), p.y, cos, sin, origin);
      return `${rotated.x} ${rotated.y}`;
    })
    .join(' L ');

  return `M ${pathCommands} Z`;
};

/**
 * SwallowTail cap component (V-shaped ribbon end)
 */
export const SwallowTailCap = ({
  point,
  angle,
  width,
  type,
}: SwallowTailCapProps) => {
  const halfWidth = width / 2;
  // Angle is already in radians from parent
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  // Mirror X for tail (points backward)
  const transformX = (x: number) => (type === 'head' ? x : -x);

  // V-shaped cap geometry
  const bodyPoints: Point[] = [
    { x: CAP_GEOMETRY.START_X_BODY, y: -halfWidth },
    { x: CAP_GEOMETRY.INNER_TIP_LENGTH, y: -halfWidth },
    { x: CAP_GEOMETRY.V_NOTCH_INNER, y: 0 },
    { x: CAP_GEOMETRY.INNER_TIP_LENGTH, y: halfWidth },
    { x: CAP_GEOMETRY.START_X_BODY, y: halfWidth },
  ];

  const bodyPath = pointsToPath(bodyPoints, transformX, cos, sin, point);

  return (
    <g>
      {/* Body gradient */}
      <path d={bodyPath} fill="url(#ribbonBodyGradient)" stroke="none" />
      {/* Sheen overlay */}
      <path
        d={bodyPath}
        fill="url(#globalSheen)"
        stroke="none"
        style={{ mixBlendMode: 'overlay' }}
      />
    </g>
  );
};
